import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import type { ChatStatus, UIMessage } from 'ai';
import AIChatMessages from '../../components/AIChatMessages';
import AIChatPromptInput from '../../components/AIChatPromptInput';
import { useAIAssistant } from '../../store';
import type { CobliChatUIMessage, SendUserTextMessage } from '../../types';

interface AIAssistantContentProps {
  emptyState: React.ReactNode;
  placeholder?: string;
  messages: UIMessage[];
  status: ChatStatus;
  error?: Error | undefined;
  sendMessage: SendUserTextMessage;
  stop: () => void;
  regenerate: () => void;
  onRetry?: () => void;
}

const AIAssistantContent = ({ emptyState, placeholder, messages, status, error, sendMessage, stop, regenerate, onRetry }: AIAssistantContentProps) => {
  const { isExpanded, isFetchingThread } = useAIAssistant();
  const { t } = useTranslation('ai');

  if (isFetchingThread)
    return (
      <div className='flex flex-1 items-center justify-center'>
        <Icon name='loader' size='md' className='animate-spin' aria-hidden='true' />
      </div>
    );

  if (error && messages.length === 0)
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center'>
        <Icon name='circle-alert' size='md' className='text-gray-9' />
        <p className='text-gray-11 text-sm'>{t('thread_error')}</p>
        {onRetry && (
          <Button variant='outlined' size='sm' onClick={onRetry}>
            {t('start_new_conversation')}
          </Button>
        )}
      </div>
    );

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {messages.length > 0 && !isFetchingThread ? (
        <div className='relative flex min-h-0 flex-1 flex-col overflow-y-auto pl-2'>
          <AIChatMessages
            hideAvatar
            className={isExpanded ? 'h-full' : ''}
            messageContentClassName='text-[13px] leading-5'
            messages={messages}
            status={status}
            hideReasoning
            onRegenerate={regenerate}
          />
          {error && (
            <div className='flex items-center justify-center gap-2 px-4 py-3'>
              <p className='text-gray-11 text-xs'>{t('send_failed')}</p>
              {onRetry && (
                <Button variant='ghost' size='sm' className='text-xs' onClick={onRetry}>
                  {t('start_new_conversation')}
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>{emptyState}</div>
      )}
      <div className='shrink-0 bg-gray-1 pb-2'>
        <AIChatPromptInput placeholder={placeholder} sendMessage={sendMessage} status={status} stop={stop} messages={messages as CobliChatUIMessage[]} />
      </div>
    </div>
  );
};

export default AIAssistantContent;
